import { useEffect, useRef } from 'react';
import clsx from 'clsx';
import { HashRouter } from 'react-router-dom';
import { t } from 'i18next';

import { getAmpConfigFlags } from 'shared/config/amp-config';
import { Navigation } from 'shared/ui/navigation';
import { useCurrentUrl, useMouseDetect } from 'shared/lib/utils';
// eslint-disable-next-line boundaries/element-types
import {
  AcceptedInviteAlert,
  useAcceptInvite,
  useIsAcceptedInviteAlertVisibleSelector,
} from 'features/accepted-invite-alert';

import styles from './styles.module.scss';

type ErrorProps = {
  isAssignmentsError?: boolean;
  resetError?: () => void;
};

export const Error = ({ isAssignmentsError, resetError }: ErrorProps) => {
  const appRef = useRef<HTMLDivElement>(null);

  const currentUrl = useCurrentUrl();

  const isAcceptedInviteAlertVisible =
    useIsAcceptedInviteAlertVisibleSelector();

  const { isNewNavigationEnabled } = getAmpConfigFlags();

  useMouseDetect(appRef);
  useAcceptInvite();

  useEffect(() => {
    const regex = /\/home\/educator\/?$/;

    const isOnHomePage = regex.test(currentUrl);

    if (!isAssignmentsError || !isOnHomePage || !resetError) return;

    resetError();
  }, [isAssignmentsError, currentUrl, resetError]);

  return (
    <section
      ref={appRef}
      className={clsx(
        'd-flex',
        'justify-content-center',
        'align-items-center',
        'page',
        'error-screen-wrap',
        {
          'error-screen-wrap-new-nav': isNewNavigationEnabled,
        },
      )}
    >
      {!isNewNavigationEnabled && (
        <HashRouter>
          <Navigation />
        </HashRouter>
      )}
      {isAcceptedInviteAlertVisible && (
        <div className="error-screen-accept-invite-message">
          <AcceptedInviteAlert />
        </div>
      )}
      <div
        className={clsx(styles.info, {
          [styles.infoWithNewNav]: isNewNavigationEnabled,
        })}
      >
        <p className={styles.text}>
          {t('Oops! Something went wrong.')}
          <br />
          {t('Please contact our support team.')}
        </p>
        <a
          href="https://my.amplify.com/help"
          className={clsx(styles.supportBtn, 'btn', 'btn-oval')}
        >
          {t('Support')}
        </a>
      </div>
    </section>
  );
};
